import { useEffect, useState } from "react";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";

const empty = { company_name: "", contact_person: "", email: "", phone: "", address: "", city: "", vat_number: "", notes: "", customer_type: "Business" };

export default function Customers() {
  const { can } = useAuth();
  const confirm = useConfirm();
  const editable = can("customers");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/customers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/customers/${editing}`, form);
      else await api.post("/customers", form);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (c) => { if (!(await confirm({ title: "Delete customer?", description: `"${c.company_name}" and its CRM record will be permanently removed.`, confirmText: "Delete" }))) return; await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(); };
  const view = async (c) => { const { data } = await api.get(`/customers/${c.id}`); setDetail(data); };

  return (
    <div>
      <PageHeader title="Customers (CRM)" subtitle={`${items.length} companies & contacts`}>
        {editable && <Button onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="add-customer-button"><Plus className="h-4 w-4 mr-1.5" /> Add Customer</Button>}
      </PageHeader>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-secondary hover:bg-secondary">
            <TableHead>Company</TableHead><TableHead>Contact</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>VAT No.</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row`} className="cursor-pointer" onClick={() => view(c)}>
                <TableCell className="font-medium">{c.company_name}</TableCell>
                <TableCell>{c.contact_person}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.email}</TableCell>
                <TableCell className="text-sm">{c.phone}</TableCell>
                <TableCell className="text-xs font-mono text-muted-foreground">{c.vat_number} {c.price_tier && c.price_tier !== "Retail" && <Badge variant="outline" className="ml-1 font-sans">{c.price_tier}</Badge>}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  {editable && <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setForm({ ...empty, ...c }); setEditing(c.id); setOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => del(c)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No customers yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "New"} Customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <F label="Company Name" cn="col-span-2"><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="customer-company-input" /></F>
            <F label="Contact Person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></F>
            <F label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email-input" /></F>
            <F label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
            <F label="VAT Number"><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></F>
            <F label="Price Tier"><Select value={form.price_tier || "Retail"} onValueChange={(v) => setForm({ ...form, price_tier: v })}><SelectTrigger data-testid="customer-tier-select"><SelectValue /></SelectTrigger><SelectContent>{["Retail", "Trade", "Contract"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></F>
            <F label="Client Type"><Select value={form.customer_type || "Business"} onValueChange={(v) => setForm({ ...form, customer_type: v })}><SelectTrigger data-testid="customer-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Business">Business</SelectItem><SelectItem value="Individual">Individual</SelectItem></SelectContent></Select></F>
            <F label="Town / City (for delivery zones)"><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Pretoria" data-testid="customer-city-input" /></F>
            <F label="Address" cn="col-span-2"><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} /></F>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="save-customer-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" /> {detail?.company_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Contact" value={detail.contact_person} />
                <Info label="Email" value={detail.email} />
                <Info label="Phone" value={detail.phone} />
                <Info label="VAT" value={detail.vat_number} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Quotation History</div>
                {detail.quotations?.length ? (
                  <div className="border border-border rounded-md divide-y divide-border max-h-48 overflow-y-auto">
                    {detail.quotations.map((q) => (
                      <div key={q.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="font-mono">{q.number}</span>
                        <Badge variant="outline">{q.status}</Badge>
                        <span className="tabular font-medium">{money(q.total)}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">No quotations yet.</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children, cn = "" }) {
  return <div className={`space-y-1.5 ${cn}`}><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>;
}
function Info({ label, value }) {
  return <div><div className="text-[10px] uppercase text-muted-foreground">{label}</div><div className="font-medium">{value || "—"}</div></div>;
}
